import { Injectable, NgZone, inject } from '@angular/core';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n/utils';
import type { TuiLanguageName } from '@taiga-ui/i18n/types';
import { RuntimeTranslationService } from './runtime-translation.service';

interface Translation {
  original: string;
  applied: string | null;
}

/** Add `translate="no"` (or class `notranslate`) to any element whose content must stay as-is. */
const SKIP_SELECTOR = 'script, style, noscript, textarea, input, code, pre, [translate="no"], .notranslate, [contenteditable="true"]';
const TRANSLATED_ATTRIBUTES = ['placeholder', 'title'];
const FLUSH_DELAY_MS = 100;

/**
 * Translates the rendered page text into the language chosen in the Taiga language switcher.
 * Angular keeps owning the DOM: when a binding rewrites a text node, the new value is
 * picked up by the MutationObserver and translated again.
 */
@Injectable({ providedIn: 'root' })
export class PageTranslationService {
  private readonly switcher = inject(TuiLanguageSwitcherService);
  private readonly translator = inject(RuntimeTranslationService);
  private readonly zone = inject(NgZone);

  private readonly texts = new Map<Text, Translation>();
  private readonly attributes = new Map<Element, Map<string, Translation>>();
  private readonly pendingRoots = new Set<Node>();
  private observer: MutationObserver | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private language: TuiLanguageName = 'english';
  private generation = 0;

  public start(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver((records) => this.onMutations(records));
      this.switcher.subscribe(() => this.apply(this.switcher.language));
    });
  }

  private apply(language: TuiLanguageName): void {
    this.zone.runOutsideAngular(() => {
      this.generation++;
      this.language = language;
      this.observer?.disconnect();
      this.pendingRoots.clear();
      this.restoreAll();

      if (!this.translator.supports(language)) {
        return;
      }

      this.observer?.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: TRANSLATED_ATTRIBUTES,
      });
      this.schedule(document.body);
    });
  }

  private onMutations(records: MutationRecord[]): void {
    for (const record of records) {
      if (record.type === 'childList') {
        record.addedNodes.forEach((node) => this.schedule(node));
      } else {
        this.schedule(record.target);
      }
    }
  }

  private schedule(node: Node): void {
    this.pendingRoots.add(node);
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
  }

  private flush(): void {
    this.flushTimer = null;
    const roots = [...this.pendingRoots];
    this.pendingRoots.clear();

    for (const root of roots) {
      if (root.isConnected) {
        this.scan(root);
      }
    }
    this.prune();
  }

  private scan(root: Node): void {
    if (root.nodeType === Node.TEXT_NODE) {
      if (!root.parentElement?.closest(SKIP_SELECTOR)) {
        this.translateText(root as Text);
      }
      return;
    }

    if (!(root instanceof Element) || root.closest(SKIP_SELECTOR)) {
      return;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return (node as Element).matches(SKIP_SELECTOR) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
      },
    });

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      this.translateText(node as Text);
    }

    this.translateAttributes(root);
    root.querySelectorAll(TRANSLATED_ATTRIBUTES.map((name) => `[${name}]`).join(',')).forEach((element) => {
      if (!element.closest(SKIP_SELECTOR)) {
        this.translateAttributes(element);
      }
    });
  }

  private translateText(node: Text): void {
    const current = node.data;
    const known = this.texts.get(node);
    if (known && (current === known.applied || current === known.original)) {
      return;
    }

    if (!isTranslatable(current)) {
      this.texts.delete(node);
      return;
    }

    const entry: Translation = { original: current, applied: null };
    this.texts.set(node, entry);

    const generation = this.generation;
    this.translator.translate(current, this.language).then((translated) => {
      if (generation !== this.generation || this.texts.get(node) !== entry || node.data !== entry.original) {
        return;
      }
      if (translated.trim() === current.trim()) {
        return;
      }
      entry.applied = withSurroundingWhitespace(current, translated.trim());
      node.data = entry.applied;
    });
  }

  private translateAttributes(element: Element): void {
    for (const name of TRANSLATED_ATTRIBUTES) {
      const current = element.getAttribute(name);
      if (current === null) continue;

      let byName = this.attributes.get(element);
      const known = byName?.get(name);
      if (known && (current === known.applied || current === known.original)) {
        continue;
      }

      if (!isTranslatable(current)) {
        byName?.delete(name);
        continue;
      }

      if (!byName) {
        byName = new Map();
        this.attributes.set(element, byName);
      }
      const entry: Translation = { original: current, applied: null };
      byName.set(name, entry);

      const generation = this.generation;
      const owner = byName;
      this.translator.translate(current, this.language).then((translated) => {
        if (generation !== this.generation || owner.get(name) !== entry || element.getAttribute(name) !== entry.original) {
          return;
        }
        if (translated.trim() === current.trim()) {
          return;
        }
        entry.applied = translated.trim();
        element.setAttribute(name, entry.applied);
      });
    }
  }

  private restoreAll(): void {
    for (const [node, entry] of this.texts) {
      if (entry.applied !== null && node.data === entry.applied) {
        node.data = entry.original;
      }
    }
    for (const [element, byName] of this.attributes) {
      for (const [name, entry] of byName) {
        if (entry.applied !== null && element.getAttribute(name) === entry.applied) {
          element.setAttribute(name, entry.original);
        }
      }
    }
    this.texts.clear();
    this.attributes.clear();
  }

  private prune(): void {
    for (const node of this.texts.keys()) {
      if (!node.isConnected) this.texts.delete(node);
    }
    for (const element of this.attributes.keys()) {
      if (!element.isConnected) this.attributes.delete(element);
    }
  }
}

/**
 * Only English source text is sent: anything without Latin letters (numbers, amounts,
 * Taiga's already-localised strings), single tokens with digits (vehicle numbers, "1m"),
 * initials, emails and URLs are left untouched.
 */
function isTranslatable(text: string): boolean {
  const value = text.trim();
  return (
    value.length > 1 &&
    value.length <= 500 &&
    /[A-Za-z]/.test(value) &&
    !/^\S*\d\S*$/.test(value) &&
    !/^[A-Z]{1,3}$/.test(value) &&
    !/@|https?:\/\/|www\./i.test(value)
  );
}

function withSurroundingWhitespace(original: string, translated: string): string {
  const leading = original.match(/^\s*/)?.[0] ?? '';
  const trailing = original.match(/\s*$/)?.[0] ?? '';
  return leading + translated + trailing;
}
